// frontend/src/features/ups/UpsReservedDriverList.js
// This React component renders the UPS Reserved drivers list, honoring global filters,
// and letting users activate drivers or—if admin—ban them via a reason modal.
//
// Phase 6:
// - READ: Reserved drivers come from assignmentsSlice (confirmed assignments) instead of driverSlice.
// - REALTIME: Option A => no component-level socket listeners (handled centrally in services/socket.js).
// - WRITES: activate/ban stay the same; after success we reload confirmed assignments so rows move immediately.
// - MODAL FIX: when opening details from an assignment-based row, upsert the driver into driverSlice first
//              so DriverDetailsModal can prefer Redux + receive live socket updates.
//
// Phase 7:
// - Use selectedSiteId (from sitesSlice) instead of hardcoding MAIN.
//
// Phase 8.7D cleanup:
// - Do NOT pass agencyId/search into loadSiteAssignments payload (thunk reads from Redux).
// - Keep effect deps on agencyId/q so refetch occurs when filters change.

// Phase 09 note:
// - This tab is intentionally NOT time-bucket-specific.
// - Reserved is a standby / capacity view, not a time-sliced operational lane.
// - Primary truth here is assignment_status = 'confirmed'.
// - Therefore we use an explicit broad query:
//     statuses: ['confirmed']
//     timeBucket: undefined
// - This makes the intent clear during Phase 09 cleanup:
//   time filtering is intentionally not applied here yet.
// - However, we still preserve canonical starts_at / ends_at on the adapted row model
//   so the UI remains aligned with the Phase 09 time model.

import React, { useEffect, useMemo, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { UserCheck, Eye, Ban as BannedIcon } from 'lucide-react';

import { selectIsUpsAdmin } from '../auth/authSlice';
import { selectAllAgencies } from '../agencies/agenciesSlice';

// ✅ Phase 7: site context
import {
  selectConcreteSelectedSiteId,
  selectIsAllSitesMode,
} from '../sites/sitesSlice';

// ✅ Phase 6: switch from drivers slice to assignments slice (read path)
import { loadSiteAssignments, makeSelectAssignmentsForTab } from '../assignments/assignmentsSlice';

import { selectFilters, bumpStatsRefreshKey } from '../ui/filtersSlice';

import { activateDriver, banDriver } from '../../services/upsDriverService';
import DriverDetailsModal from './modals/DriverDetailsModal';

// ✅ Modal live-update fix: prime driverSlice for DriverDetailsModal
import { upsertDriver } from '../drivers/driverSlice';
import { formatOwnerLabel } from './utils/formatOwnerLabel';
import DriverMobileCard from './components/DriverMobileCard';

const normalizeId = (v) => (v == null ? undefined : Number(v));

const RESERVED_ASSIGNMENT_QUERY = {
  statuses: ['confirmed'],
  timeBucket: undefined,
};

export default function UpsReservedDriverList() {
  const dispatch = useDispatch();
  const banReasonId = 'reserved-driver-ban-reason';
  const isAdmin = useSelector(selectIsUpsAdmin);
  const agencies = useSelector(selectAllAgencies);
  const { agencyId, q, source } = useSelector(selectFilters);

  // SiteSelector
  const selectedSiteId = useSelector(selectConcreteSelectedSiteId);
  const isAllSitesMode = useSelector(selectIsAllSitesMode);

  useEffect(() => {
    if (!isAllSitesMode && !selectedSiteId) return;

    dispatch(loadSiteAssignments({ ...RESERVED_ASSIGNMENT_QUERY }));
  }, [dispatch, agencyId, q, source, selectedSiteId, isAllSitesMode]);

  // ✅ Select "confirmed" assignment rows from assignmentsSlice
  const selectReservedAssignments = useMemo(() => makeSelectAssignmentsForTab('confirmed'), []);
  const rows = useSelector(selectReservedAssignments);

  // ✅ Adapt confirmed assignment rows into a UI row model with embedded driver fields.
  // Operational truth remains assignment_status; this is not a driver-status row.
  // Internal/company-owned reserved rows should render deliberately, not as blank agency values
  const reservedAssignments = useMemo(
    () =>
      (rows || [])
        // ✅ Reserved tab must not show banned drivers (banned belongs in Banned tab)
        .filter((r) => !r?.is_banned)
        .map((r) => ({
          // driver fields (your backend row likely returns driver columns)
          id: normalizeId(r.id),
          name: r.name,
          vehicle_registration_number: r.vehicle_registration_number,
          vrn: r.vrn, // fallback
          phone: r.phone,
          email: r.email,
          submission_date: r.submission_date,
          agency_id: r.agency_id,
          agency_name: r.agency_name,
          // Phase 9.1 ownership context
          workforce_source: r.workforce_source ?? null,
          company_id: normalizeId(r.company_id),
          photo_url: r.photo_url,
          photo_mime: r.photo_mime,
          photo_updated_at: r.photo_updated_at,

          // assignment metadata (useful later)
          assignment_id: r.assignment_id,
          assignment_status: r.assignment_status,
          site_id: normalizeId(r.site_id),
          site_name: r.site_name ?? null,

          // Phase 09 canonical time carry-through
          starts_at: r.starts_at ?? r.start_time ?? null,
          ends_at: r.ends_at ?? r.end_time ?? null,

          // Keep legacy time fields during dual-read transition
          start_time: r.start_time ?? null,
          end_time: r.end_time ?? null,

          // ban metadata (useful later)
          is_banned: r.is_banned,
          ban_id: r.ban_id,
          ban_scope: r.ban_scope,
          ban_site_id: r.ban_site_id,
          ban_reason: r.ban_reason,
          banned_at: r.banned_at,
        })),
    [rows]
  );

  // Focus-on-arrival via ?focus=<id>
  const [sp, setSp] = useSearchParams();
  useEffect(() => {
    const focus = sp.get('focus');
    if (!focus) return;
    const el = document.querySelector(`[data-row-id="${focus}"]`);
    if (!el) return;
    el.scrollIntoView({ block: 'center', behavior: 'smooth' });
    el.classList.add('ring-2', 'ring-blue-400', 'animate-pulse');
    const t = setTimeout(() => {
      el.classList.remove('ring-2', 'ring-blue-400', 'animate-pulse');
      const next = new URLSearchParams(sp);
      next.delete('focus');
      setSp(next, { replace: true });
    }, 1600);
    return () => clearTimeout(t);
  }, [sp, setSp]);

    // Reload through the shared thunk so the current site/all-sites mode is respected.
  const reloadReservedAssignments = () => {
  dispatch(loadSiteAssignments({ ...RESERVED_ASSIGNMENT_QUERY }));
};

  // Use the row's own site_id for writes so actions remain safe in all-sites mode.
  // Fallback to selectedSiteId keeps single-site/legacy rows working during transition.
  const handleActivate = async (row) => {
    const actionSiteId = row?.site_id || selectedSiteId;
    if (!row?.id || !actionSiteId) return;

    try {
      await activateDriver(row.id, actionSiteId);
      reloadReservedAssignments();
      dispatch(bumpStatsRefreshKey());
    } catch (err) {
      console.error('Activate failed:', err);
    }
  };

  // Ban modal (same UX as Organized list)
  const [showBanModal, setShowBanModal] = useState(false);
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [banReason, setBanReason] = useState('');

  const openBanModal = (driver) => {
    if (!isAdmin) return;

    // Prime driverSlice so any follow-up modals / details have a live Redux-backed entity
    dispatch(upsertDriver(driver));

    setSelectedDriver(driver);
    setBanReason('');
    setShowBanModal(true);
  };

  const handleBanConfirm = async () => {
    if (!isAllSitesMode && !selectedSiteId) return;
    if (!banReason.trim() || !isAdmin) return;

    try {
      await banDriver(selectedDriver.id, banReason);

      // Phase 5: Ban is company-scoped, then reload through the mode-aware assignment thunk.
      reloadReservedAssignments();
      dispatch(bumpStatsRefreshKey());

      setShowBanModal(false);
      setSelectedDriver(null);
    } catch (err) {
      console.error('Ban failed:', err);
    }
  };

  // Details modal
  const [showDetails, setShowDetails] = useState(false);
  const [detailsDriver, setDetailsDriver] = useState(null);

  // ✅ Phase 6 fix: ensure the driver exists in driverSlice so the details modal can
  // prefer Redux and receive live socket updates (photo updates, driverUpdated, etc.)
  const openDetails = (driver) => {
    dispatch(upsertDriver(driver));
    setDetailsDriver(driver);
    setShowDetails(true);
  };

  const closeDetails = () => {
    setShowDetails(false);
    setDetailsDriver(null);
  };

  // Empty-state labels
  const scopeLabel = (() => {
    if (!agencyId) return 'All Agencies';
    const a = (agencies || []).find(
      (x) => String(x.id ?? x.agency_id ?? x.agencyId) === String(agencyId)
    );
    return a?.name ?? a?.agency_name ?? `Agency #${agencyId}`;
  })();

  const hasSearch = (q || '').trim().length > 0;

  return (
    <>
      <section className="my-6 overflow-hidden rounded-2xl border border-app-border bg-app-card shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-4 border-b border-app-border px-6 py-5">
          <div>
            <h3 className="text-xl font-bold text-app-text">Reserved Queue</h3>
            <p className="mt-1 text-sm text-app-muted">
              Review standby drivers and activate them when capacity is needed.
            </p>
          </div>

          <div className="rounded-full bg-app-surface px-4 py-2 text-sm font-semibold text-app-muted">
            {reservedAssignments.length} reserved
          </div>
        </div>

        <div className="hidden overflow-x-auto xl:block">
          <table className="min-w-full bg-app-card">
            <thead className="border-b border-app-border bg-app-surface">
              <tr>
                {['Driver', 'Vehicle', 'Phone', 'Email', 'Owner', 'Site', 'Submitted', 'Actions'].map((col) => (
                  <th
                    key={col}
                    className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-[0.18em] text-app-muted"
                  >
                    {col}
                  </th>
                ))}
              </tr>
            </thead>

            <tbody className="divide-y divide-app-border">
              {reservedAssignments.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-6 py-12 text-center text-sm text-app-muted">
                    No reserved drivers{agencyId ? ` for ${scopeLabel}` : ''}
                    {hasSearch ? ` matching “${q}”` : ''}.
                  </td>
                </tr>
              ) : (
                reservedAssignments.map((row) => (
                  <tr
                    key={row.id}
                    data-row-id={row.id}
                    className="transition hover:bg-app-surface"
                  >
                    <td className="px-6 py-5 whitespace-nowrap">
                      <div className="flex items-center gap-3">
                        <div className="flex h-10 w-10 items-center justify-center rounded-full border border-app-accent bg-app-primary text-xs font-bold uppercase text-app-accent">
                          {(row.name || '—')
                            .split(' ')
                            .map((part) => part[0])
                            .join('')
                            .slice(0, 2)}
                        </div>

                        <div>
                          <div className="font-semibold text-app-text">{row.name || '—'}</div>
                          <div className="text-xs text-app-muted">
                            Driver ID · {row.id ? `DRV-${row.id}` : '—'}
                          </div>
                        </div>
                      </div>
                    </td>

                    <td className="px-6 py-5 whitespace-nowrap text-sm text-app-text">
                      {row.vehicle_registration_number || row.vrn || '—'}
                    </td>

                    <td className="px-6 py-5 whitespace-nowrap text-sm text-app-text">
                      {row.phone || '—'}
                    </td>

                    <td className="px-6 py-5 whitespace-nowrap text-sm text-app-muted">
                      {row.email || '—'}
                    </td>

                    <td className="px-6 py-5 whitespace-nowrap text-sm text-app-text">
                      {formatOwnerLabel(row)}
                    </td>

                    <td className="px-6 py-5 whitespace-nowrap text-sm text-app-text">
                      {row.site_name || '—'}
                    </td>

                    <td className="px-6 py-5 whitespace-nowrap text-sm text-app-text">
                      {row.submission_date ? new Date(row.submission_date).toLocaleString() : '—'}
                    </td>

                    <td className="px-6 py-5 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => openDetails(row)}
                          aria-label="View driver details"
                          title="View details"
                          className="rounded-xl border border-app-border bg-app-card px-3 py-2 text-sm font-semibold text-app-text transition hover:bg-app-surface"
                        >
                          <Eye size={16} />
                        </button>

                        <button
                          onClick={() => handleActivate(row)}
                          aria-label="Activate driver"
                          title={row.is_banned ? `Banned: ${row.ban_reason || '—'}` : 'Activate'}
                          disabled={!!row.is_banned}
                          className={
                            'rounded-xl border px-3 py-2 text-sm font-semibold transition ' +
                            (row.is_banned
                              ? 'cursor-not-allowed border-app-border bg-app-surface text-app-muted/50'
                              : 'border-app-border bg-app-card text-green-700 hover:bg-app-surface')
                          }
                        >
                          <UserCheck size={16} />
                        </button>

                        <button
                          onClick={() => openBanModal(row)}
                          aria-label="Ban driver"
                          title={isAdmin ? 'Ban' : 'Admins only'}
                          disabled={!isAdmin}
                          className={
                            'rounded-xl border px-3 py-2 text-sm font-semibold transition ' +
                            (isAdmin
                              ? 'border-red-200 bg-app-card text-red-600 hover:bg-red-50'
                              : 'cursor-not-allowed border-app-border bg-app-surface text-app-muted/50')
                          }
                        >
                          <BannedIcon size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="space-y-3 p-4 xl:hidden">
          {reservedAssignments.length === 0 ? (
            <div className="rounded-2xl border border-app-border bg-app-surface px-4 py-10 text-center text-sm text-app-muted">
              No reserved drivers{agencyId ? ` for ${scopeLabel}` : ''}
              {hasSearch ? ` matching "${q}"` : ''}.
            </div>
          ) : (
            reservedAssignments.map((row) => {
              const avatarText = (row.name || '')
                .split(' ')
                .map((part) => part[0])
                .join('')
                .slice(0, 2);

              return (
                <div key={row.id} data-row-id={row.id}>
                  <DriverMobileCard
                    title={row.name || '—'}
                    subtitle={`Driver ID · ${row.id ? `DRV-${row.id}` : '—'}`}
                    avatarText={avatarText}
                    fields={[
                      ['Vehicle', row.vehicle_registration_number || row.vrn],
                      ['Owner', formatOwnerLabel(row)],
                      ['Site', row.site_name],
                      ['Phone', row.phone],
                      ['Email', row.email],
                      ['Submitted', row.submission_date ? new Date(row.submission_date).toLocaleString() : null],
                      ['Assignment Status', row.assignment_status],
                    ]}
                    badges={
                      row.is_banned
                        ? [
                            <span
                              key="banned"
                              className="inline-flex items-center rounded-full border border-red-200 bg-red-50 px-3 py-1 text-xs font-semibold text-red-700"
                            >
                              Banned
                            </span>,
                          ]
                        : []
                    }
                    actions={
                      <>
                        <button
                          onClick={() => openDetails(row)}
                          aria-label="View driver details"
                          title="View details"
                          className="rounded-xl border border-app-border bg-app-card px-3 py-2 text-sm font-semibold text-app-text transition hover:bg-app-surface"
                        >
                          <Eye size={16} />
                        </button>

                        <button
                          onClick={() => handleActivate(row)}
                          aria-label="Activate driver"
                          title={row.is_banned ? `Banned: ${row.ban_reason || '—'}` : 'Activate'}
                          disabled={!!row.is_banned}
                          className={
                            'rounded-xl border px-3 py-2 text-sm font-semibold transition ' +
                            (row.is_banned
                              ? 'cursor-not-allowed border-app-border bg-app-surface text-app-muted/50'
                              : 'border-app-border bg-app-card text-green-700 hover:bg-app-surface')
                          }
                        >
                          <UserCheck size={16} />
                        </button>

                        <button
                          onClick={() => openBanModal(row)}
                          aria-label="Ban driver"
                          title={isAdmin ? 'Ban' : 'Admins only'}
                          disabled={!isAdmin}
                          className={
                            'rounded-xl border px-3 py-2 text-sm font-semibold transition ' +
                            (isAdmin
                              ? 'border-red-200 bg-app-card text-red-600 hover:bg-red-50'
                              : 'cursor-not-allowed border-app-border bg-app-surface text-app-muted/50')
                          }
                        >
                          <BannedIcon size={16} />
                        </button>
                      </>
                    }
                  />
                </div>
              );
            })
          )}
        </div>
      </section>

      {/* Ban Modal */}
      {showBanModal && (
        <div
          className="fixed inset-0 z-50 flex items-end justify-center bg-black/45 p-0 backdrop-blur-sm sm:items-center sm:p-4"
          onClick={() => setShowBanModal(false)}
        >
          <div
            className="flex h-[70dvh] w-full flex-col overflow-hidden rounded-t-2xl border border-app-border bg-app-card text-app-text shadow-2xl sm:h-auto sm:max-h-[92dvh] sm:max-w-md sm:rounded-2xl"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="reserved-ban-title"
          >
            <div className="flex shrink-0 items-center justify-between gap-4 border-b border-app-border px-4 py-4 sm:px-5">
              <h2 id="reserved-ban-title" className="text-xl font-semibold text-app-text">
                Ban {selectedDriver?.name}
              </h2>
              <button
                onClick={() => setShowBanModal(false)}
                className="rounded-xl px-3 py-2 text-xl font-bold text-app-muted transition hover:bg-app-surface hover:text-app-text"
                aria-label="Close ban modal"
              >
                &times;
              </button>
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4 sm:px-5 sm:py-5">
              <label htmlFor={banReasonId} className="mb-2 block text-sm font-medium text-app-muted">
                Ban reason
              </label>
              <textarea
                id={banReasonId}
                name="banReason"
                rows={4}
                placeholder="Enter ban reason"
                value={banReason}
                onChange={(e) => setBanReason(e.target.value)}
                className="w-full rounded-xl border border-app-border bg-app-card px-4 py-3 text-sm text-app-text outline-none transition placeholder:text-app-muted focus:border-app-primary focus:ring-2 focus:ring-app-primary/20"
              />
            </div>

            <div
              className="flex shrink-0 flex-wrap items-center justify-end gap-2 border-t border-app-border bg-app-surface px-4 py-3 sm:px-5 sm:py-4"
              style={{ paddingBottom: 'calc(0.75rem + env(safe-area-inset-bottom))' }}
            >
              <button
                onClick={() => setShowBanModal(false)}
                className="rounded-xl border border-app-border bg-app-card px-4 py-2.5 text-sm font-semibold text-app-text transition hover:bg-app-surface"
              >
                Cancel
              </button>
              <button
                onClick={handleBanConfirm}
                disabled={!isAdmin}
                title={isAdmin ? 'Confirm Ban' : 'Admins only'}
                className={
                  'rounded-xl px-5 py-2.5 text-sm font-bold shadow-sm transition ' +
                  (isAdmin ? 'bg-red-600 text-white hover:bg-red-700' : 'cursor-not-allowed bg-app-surface text-app-muted/50')
                }
              >
                Confirm Ban
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Driver Details Modal — standalone */}
      {showDetails && detailsDriver && <DriverDetailsModal driver={detailsDriver} onClose={closeDetails} />}
    </>
  );
}
